import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import BookingForm from "./BookingForm";

const Bookings = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const columns = [
    { title: "Booking ID", field: "booking_id" },
    { title: "Source ID", field: "source_id" },
    { title: "Source A ID", field: "source_a_id" },
    { title: "Text", field: "text" },
    { title: "Channel ID", field: "channel_id" },
    { title: "Reservation ID", field: "reservation_id" },
    { title: "Guest Email", field: "guest_email" },
    { title: "Guest Phone", field: "guest_phone" },
    { title: "Total Paid", field: "total_paid" },
    { title: "Commission Amount", field: "commission_amount" },
    { title: "Currency", field: "currency" },
    { title: "Exchange Rate", field: "exchange_rate" },
    { title: "Booking Date", field: "booking_date" },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/BookingSource");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      const payload = {
        data: {
          ...formData,
          booking_id: formData.booking_id
            ? parseInt(formData.booking_id)
            : undefined,
          channel_id: parseInt(formData.channel_id),
          source_id: parseInt(formData.source_id),
          source_a_id: formData.source_a_id,
          text: formData.text,
        },
      };

      if (editData?.booking_id) {
        const response = await fetch(
          `https://api.esthell.com/BookingSource/${editData.booking_id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update booking");
        }
        setNotification({
          open: true,
          message: "Booking updated successfully!",
          type: "success",
        });
      } else {
        const response = await fetch(
          "https://api.esthell.com/create/BookingSource",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create booking");
        }
        setNotification({
          open: true,
          message: "Booking created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/BookingSource/${row.booking_id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete booking");
      }

      const result = await response.json();
      if (result.success) {
        fetchData();
        setNotification({
          open: true,
          message: "Booking deleted successfully!",
          type: "success",
        });
      } else {
        throw new Error(result.message || "Failed to delete booking");
      }
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Bookings
        </Typography>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <BookingForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            handleSubmit={handleFormSubmit} // Changed from onSubmit to handleSubmit
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Bookings;
